import type { Emitter } from 'mitt'
import mitt from 'mitt'
import type { Feature, FeatureCollection, Geometry } from 'geojson'
import type L from 'leaflet'
import type { ProjectionLike } from 'ol/proj'
import type { BaseModel } from '../models/BaseModel'
import type { ApiService } from '../utils/api.service'
import type { Sort } from '../types/Api/Query/Sort'
import type { Pagination } from '../types/Api/Query/Pagination'
import { I3pEventsEnum } from '../types/I3pEvents.enum'
import type { ShowMeta } from '../types/Api/Response/ShowMeta'
import type { IndexResponse } from '../types/Api/Response/IndexResponse'
import type { Filter } from '../types/Api/Response/Filter'
import type { I3pServiceInterface } from './i3p.service.interface'
import NavigationMode = Datakode.LaravelDatakodeUtils.Enum.NavigationMode

// noinspection JSUnusedGlobalSymbols
export abstract class I3pService<T> implements I3pServiceInterface {
    public currentEntity: BaseModel<T> | null = null
    public currentEntityK: string | null = null
    public editMode: boolean | null = false
    public isSaving: boolean | null = false

    public indexData: IndexResponse<T> | null = null
    public indexPositionData: string[] | null = null
    public geojsonData: FeatureCollection | null = null

    public emitter: Emitter<I3pEventsEnum, any> = mitt()

    private _pagination: Pagination = {
        number: 1,
        size: 10,
    }

    private _sort: Sort | null
    private _initialQuery: any = {}
    private _query: any = {}
    private _hiddenFilters: string[] = []
    private _navigationMode: NavigationMode = 'displayAll'
    private _map: Map | null = null

    private readonly $api: ApiService

    public creationSlug: string = 'creation'

    protected constructor() {
        const { $api } = useNuxtApp()
        this.$api = $api
    }

    setEditMode(value: boolean): boolean {
        const editMode = this.editMode = value
        if (!editMode && this.isCreation()) {
            this.selectNothing()
        }
        this.emitter.emit(I3pEventsEnum.switchEditMode, { editMode })
        return this.editMode
    }

    setIsSaving(value: boolean): boolean {
        this.isSaving = value
        return this.isSaving
    }

    selectNothing(): void {
        this.currentEntity = null
        this.currentEntityK = null
        this.emitter.emit(I3pEventsEnum.currentEntityUpdated, { currentEntity: null })
    }

    setPagination(value: Pagination): this {
        if (this._pagination === value) {
            return
        }
        this._pagination = value
        this.indexData = null
        return this
    }

    getPagination(): Pagination {
        return this._pagination
    }

    setSort(value: Sort | null): this {
        if (this._sort === value) {
            return
        }
        this._sort = value
        this.indexData = null
        this.indexPositionData = null
        return this
    }

    getSort(): Sort {
        return this._sort
    }

    resetQuery(): void {
        this.setQuery(this._initialQuery).loadData(true).then()
    }

    setInitialQuery(value: any[]): this {
        this._initialQuery = value
        return this.setQuery(this._initialQuery)
    }

    setQuery(value: any): this {
        if (this._query === value)
            return this // NEEDED TO CHAIN FUNCTION AS loadData()

        this._query = value
        this.indexData = null
        this.indexPositionData = null
        this.geojsonData = null
        return this
    }

    setHiddenFilters(filterNames: string[]): this {
        this._hiddenFilters = filterNames
        return this
    }

    getHiddenFilters(): string[] {
        return this._hiddenFilters
    }

    getQuery(): any {
        return this._query
    }

    newEntity(data: object | null = null, meta: ShowMeta | null = null): Model<T> {
        const entity = this.getModel() as BaseModel
        entity.data = data
        entity.meta = meta
        return entity
    }

    setCurrentEntity(entity: Model<T> | null) {
        // if (entity !== null && entity?.getKey() === this.currentEntityK) {
        //     return
        // }
        if (this.currentEntityK !== this.creationSlug) { // enable create entity to be editable
            this.setEditMode(false)
        }
        if (entity) {
            entity.loaded = true
            const currentEntity = this.currentEntity = entity
            this.currentEntityK = entity?.getKey()
            this._updatePaginationOnCurrentEntity()
            this.emitter.emit(I3pEventsEnum.currentEntityUpdated, { currentEntity })
        }
        else {
            if (this.currentEntityK) {
                return // prevent infinite load next
            }
            const nextKey = this.getNextKey()
            if (nextKey) {
                this.showEntity(nextKey).then()
            }
            else {
                const previousKey = this.getPreviousKey()
                if (previousKey) {
                    this.showEntity(previousKey).then()
                }
                else {
                    this.currentEntityK = null
                    this.emitter.emit(I3pEventsEnum.currentEntityUpdated, { currentEntity: null })
                }
            }
        }
    }

    showEntity(key: string | number | null): Promise<BaseModel<T> | null | false> {
        if (!this.indexPositionData) {
            this.setCurrentEntity(null)
            return Promise.resolve(null)
        }
        if (key !== null && key === this.currentEntityK) { // already loaded
            return Promise.resolve(false)
        }
        const currentEntityK = this.currentEntityK = key
        const keyExist = this.indexPositionData.includes(key)
        if (!keyExist) {
            this.loadFirstItem()
            return Promise.resolve(null)
        }

        this.emitter.emit(I3pEventsEnum.currentEntityUpdating, { currentEntityK })
        return new Promise<BaseModel<T> | null>((resolve) => {
            const model = this.newEntity() as BaseModel<T>
            model.get(key).then((entity) => {
                if (key === this.currentEntityK) { // check if another showEntity has not been called
                    this.setCurrentEntity(entity)
                    resolve(this.currentEntity)
                }
                else {
                    console.warn(`loading of ${key} cancelled by loading of ${this.currentEntityK}`)
                    resolve(false)
                }
            })
        })
    }

    initFromRouteKey(k: string | number | null): void {
        this.currentEntityK = k
        this.loadData(!k)
    }

    loadData(loadFirstItem: boolean | null = false): Promise<void> {
        this.emitter.emit(I3pEventsEnum.allDataUpdating, {})
        if (loadFirstItem) {
            this.currentEntity = null
            this.currentEntityK = null
        }

        const promises: Promise[] = []
        if (this.indexData === null)
            promises.push(this.getIndex())

        if (this.indexPositionData === null)
            promises.push(this.getIndexPositions())

        if (this.geojsonData === null)
            promises.push(this.getGeojson())

        return new Promise<void>((resolve) => {
            Promise.all(promises).then(() => {
                if (promises.length) {
                    const indexData = this.indexData
                    this.emitter.emit(I3pEventsEnum.allDataUpdated, { indexData })
                    if (!this.currentEntityK) {
                        this.loadFirstItem()
                    }
                }
                resolve()
            })
        })
    }

    getIndex(): Promise<IndexResponse<T>> {
        return new Promise<IndexResponse<T>>((resolve) => {
            this.emitter.emit(I3pEventsEnum.indexDataUpdating, {})
            const model = this.newEntity() as BaseModel<T>
            return model.indexData(
                this._pagination,
                this._sort,
                this._query,
            ).then((value: IndexResponse<T>) => {
                const indexData: IndexResponse<T> = this.indexData = value
                this.emitter.emit(I3pEventsEnum.indexDataUpdated, { indexData })
                resolve(this.indexData)
            })
        })
    }

    getIndexPositions(): Promise<string[]> {
        return new Promise<string[]>((resolve) => {
            this.emitter.emit(I3pEventsEnum.indexPositionUpdating, {})
            const model = this.newEntity() as BaseModel<T>
            return model.indexPositionsData(
                this._sort,
                this._query,
            ).then((value) => {
                const indexPositionData = this.indexPositionData = value
                this.emitter.emit(I3pEventsEnum.indexPositionUpdated, { indexPositionData })
                resolve(this.indexPositionData)
            })
        })
    }

    getGeojson(): Promise<any> {
        return new Promise<any>((resolve) => {
            this.emitter.emit(I3pEventsEnum.geojsonUpdating, {})
            const model = this.newEntity() as BaseModel<T>
            return model.getGeojson(
                this._query,
            ).then((value: FeatureCollection) => {
                const geojsonData: FeatureCollection = this.geojsonData = value
                this.emitter.emit(I3pEventsEnum.geojsonUpdated, { geojsonData })
                resolve(this.geojsonData)
            })
        })
    }

    /**
     * create empty entity
     */
    createEntity(): Promise<BaseModel<T>> {
        return new Promise<BaseModel<T>>((resolve) => {
            const newEntity = this.newEntity()
            newEntity.getCreate()
                .then((res: BaseModel<T>) => {
                    this.setCurrentEntity(res)
                    this.currentEntityK = this.creationSlug
                    const entity = this.currentEntity
                    // this.refreshData().then()
                    resolve(entity)
                })
        })
    }

    isCreation(): boolean {
        return this.currentEntityK === this.creationSlug
    }

    reloadCurrenEntity(): Promise<BaseModel<T>> {
        const k = this.currentEntityK
        this.currentEntityK = null
        return this.showEntity(k).then()
    }

    cancelEditEntity() {
        if (!this.currentEntityK || !Number.parseInt(this.currentEntityK)) { // parseInt to prevent k as "creation"
            this.showEntity(null).then()
        }
        this.setEditMode(false)
    }

    beforeSaveEntity() {
        this.emitter.emit(I3pEventsEnum.beforeSaveEntity)
    }

    updateEntity(entity: BaseModel<T>, forceData: object | null = null): Promise<BaseModel<T>> {
        return new Promise<BaseModel<T> | null>((resolve) => {
            const data = forceData ?? entity.getEditableDataFormat()
            entity.put(data).then((entity) => {
                this.setCurrentEntity(entity)
                this.refreshData().then(() => {
                    const entity = this.currentEntity
                    resolve(entity)
                    this.emitter.emit(I3pEventsEnum.entityUpdated, { entity })
                })
            })
        })
    }

    storeEntity(entity: BaseModel<T>, forceData: object | null = null): Promise<BaseModel<T> | null> {
        return new Promise<BaseModel<T> | null>((resolve) => {
            const data = forceData ?? entity.getEditableDataFormat()
            entity.post(data).then((entity) => {
                if (!entity) {
                    resolve(null)
                    return
                }
                this.currentEntityK = entity.data.k
                this.currentEntity = entity
                this.refreshData().then(() => {
                    resolve(entity)
                    this.emitter.emit(I3pEventsEnum.entityCreated, { entity })
                })
            })
        })
    }

    deleteEntity(entity: BaseModel<T>): Promise<void> {
        const k: string = entity.getKey()
        return new Promise<void>((resolve) => {
            entity.delete().then(() => {
                this.setCurrentEntity(null)
                this.refreshData().then(() => {
                    resolve()
                    this.emitter.emit(I3pEventsEnum.entityDeleted, { k })
                })
            })
        })
    }

    public loadFirstItem() {
        if (!this.indexData?.data?.length)
            return

        const first = this.getModel().newStatic(this.indexData.data[0])
        this.showEntity(first.getKey()).then()
    }

    public getNextKey(): string | null {
        if (!this.indexPositionData) {
            return null
        }
        return this.indexPositionData[this._getCurrentItemPosition() + 1]
    }

    public getPreviousKey(): string | null {
        if (!this.indexPositionData) {
            return null
        }
        return this.indexPositionData[this._getCurrentItemPosition() - 1]
    }

    public triggerGeometryChanged(geometry: any[]): void {
        this.emitter.emit(I3pEventsEnum.geometryChanged, { geometry })
    }

    private _getCurrentItemPosition(): number | null {
        if (!this.indexPositionData) {
            return null
        }
        const index = this.indexPositionData.findIndex(el => el === this.currentEntityK) || -1 // indexPositionData is null on refreshData
        return index === -1 ? null : index
    }

    public refreshData(): Promise<void> {
        this.indexData = null
        this.indexPositionData = null
        this.geojsonData = null
        return this.loadData()
    }

    public resizeMap() {
        this.emitter.emit(I3pEventsEnum.resizeMap)
    }

    public mapFocusOnObject(geometry: Geometry) {
        this.emitter.emit(I3pEventsEnum.mapFocusOnObject, { geometry })
    }

    public mapZoomLevel(zoomLevel: number) {
        this.emitter.emit(I3pEventsEnum.mapChangeZoom, { zoomLevel })
    }

    public mapFocusBbox(bbox: L.Bounds) {
        this.emitter.emit(I3pEventsEnum.mapFocusBbox, { bbox })
    }

    public getBBox(entityK: string | number): L.LatLngBounds | null {
        // BBOX ERROR ON DELETE (surement à cause du format de geometry au getentity)
        const feature = this.getGeojsonFeature(entityK)
        return feature ? L.geoJson(feature).getBounds() : null
    }

    public getCurrentEntityGeometry(): Geometry {
        if (this.currentEntityK) {
            const geojson = { ...this.getGeojsonFeature(this.currentEntityK)?.geometry } || {}
            delete geojson.pixi
            delete geojson.projectedCoordinates
            return geojson
        }
        return null
    }

    private getGeojsonFeature(entityK: string | number): Feature | null {
        if (this.geojsonData) {
            const features = this.geojsonData.features

            for (let i = 0; i < features.length; i++) {
                const feature = features[i]
                if (feature.properties.k === entityK)
                    return feature
            }
        }
        return null
    }

    private _updatePaginationOnCurrentEntity() {
        const page = Math.floor(this._getCurrentItemPosition() / this._pagination.size) + 1
        if (page !== this._pagination.number)
            this.setPagination({ number: page, size: this._pagination.size }).loadData().then()
    }

    public getAvailableFilters(): Filter[] | null {
        return this.indexData?.meta.allowed_filters.filter((filter: Filter) => {
            return !this._hiddenFilters.includes(filter.name)
        })
    }

    public getDataProjection(): ProjectionLike {
        // geojson is in WGS83 (EPSG: 4326)
        return 'EPSG:4326' as ProjectionLike
    }

    public setNavigationMode(navigationMode: NavigationMode): void {
        this._navigationMode = navigationMode
    }

    public getNavigationMode(): NavigationMode {
        return this._navigationMode
    }

    public setMap(map: Map): void {
        this._map = map
    }

    public getMap(): Map {
        return this._map
    }
}
